import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandableChips from "components/ExpandableChips";
import IdBadge from "components/IdBadge";
import { useMemo, useState } from "react";
import CreateSystemDialog from "./CreateSystemDialog";
import { regionKey, useLidarClearance } from "./LidarClearanceContext";
import RegionFilterSelect from "./RegionFilterSelect";
import SortableHeaderCell from "./SortableHeaderCell";
import SystemDetailsDrawer from "./SystemDetailsDrawer";
import TableFilterField from "./TableFilterField";
import type { LidarClearanceSystem } from "./schemas";
import { createSortComparators } from "./sortComparators";
import t from "./translations";
import { useSortAndFilter } from "./useSortAndFilter";

const COLUMN_COUNT = 3;

type SystemSortKey = "id" | "name";

const SORT_COMPARATORS = createSortComparators<LidarClearanceSystem, SystemSortKey>({
  id: (system) => system.id,
  name: (system) => system.name,
});

function matchesFilter(system: LidarClearanceSystem, filter: string): boolean {
  return (
    String(system.id).includes(filter) ||
    `${system.region}-${system.id}`.toLowerCase().includes(filter) ||
    system.name.toLowerCase().includes(filter)
  );
}

export default function SystemsTab() {
  const { systems, systemAxisLinksBySystem, regionTags, loading } =
    useLidarClearance();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [regionFilter, setRegionFilter] = useState("");

  const regionFilteredSystems = useMemo(
    () =>
      regionFilter
        ? systems.filter((system) => system.region === regionFilter)
        : systems,
    [systems, regionFilter]
  );

  const { filterText, setFilterText, sortKey, sortDirection, toggleSort, visibleRows } =
    useSortAndFilter<LidarClearanceSystem, SystemSortKey>({
      rows: regionFilteredSystems,
      matchesFilter,
      comparators: SORT_COMPARATORS,
    });

  const selectedSystem =
    systems.find(
      (system) => regionKey(system.region, system.id) === selectedKey
    ) ?? null;

  return (
    <Box data-testid="systems-tab">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <RegionFilterSelect
            regionTags={regionTags}
            value={regionFilter}
            onChange={setRegionFilter}
            testId="systems-tab.region-filter"
          />
          <TableFilterField
            value={filterText}
            onChange={setFilterText}
            placeholder={t.FilterByIdOrNamePlaceholder}
            testId="systems-tab.filter"
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          data-testid="systems-tab.new-system"
        >
          {t.NewSystemButton}
        </Button>
      </Box>
      <TableContainer component={Paper} data-testid="systems-table">
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              <SortableHeaderCell
                sortKeyName="id"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={toggleSort}
                align="center"
                sx={{ width: 90 }}
              >
                ID
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKeyName="name"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={toggleSort}
                sx={{ minWidth: 200 }}
              >
                {t.NameColumn}
              </SortableHeaderCell>
              <TableCell sx={{ width: 400 }}>{t.AxesColumn}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {systems.length === 0 ? "—" : t.NoMatchingRows}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map((system) => {
              const key = regionKey(system.region, system.id);
              // Axis ids are only unique within a region, but every axis
              // linked to one system is necessarily in that system's own
              // region, so a plain id key is safe for this per-row lookup.
              const links = systemAxisLinksBySystem.get(key) ?? [];
              const axisIds = links.map((entry) => entry.axis.id);
              const axisLabelMap = new Map(
                links.map((entry) => [entry.axis.id, entry.axis.name_en])
              );

              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => setSelectedKey(key)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`systems-tab.row-${system.region}-${system.id}`}
                >
                  <TableCell align="center">
                    <IdBadge
                      prefix={system.region.toUpperCase()}
                      id={system.id}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {system.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ExpandableChips
                      ids={axisIds}
                      labelMap={axisLabelMap}
                      max={3}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <SystemDetailsDrawer
        system={selectedSystem}
        onClose={() => setSelectedKey(null)}
      />
      <CreateSystemDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
